import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, input, output, signal, viewChild } from '@angular/core';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { DataSelectorDirective } from '../../../directives/data-selector/data-selector.directive';
import { SharedModule } from '../../../shared.module';
import { applicationDebounceTime } from '../../../utils/functions';

/**
 * Presentational floating controls for the Apryse previewer.
 *
 * The host is a full-bleed, click-through overlay (`pointer-events: none`); each individual
 * bar re-enables `pointer-events: auto` so the document stays scrollable/grab-able in the gaps.
 * It renders three independent floating elements:
 *
 *  - a top-center pill that morphs in place between a zoom group and a search group,
 *  - circular previous/next page buttons hugging the left/right edges,
 *  - a bottom-center "page X of N" pill that turns into a jump-to-page input on click.
 *
 * Pure: no Apryse import, no viewer state. Inputs in, events out. The only local state is
 * presentational UI toggles (`searchOpen`, `jumpEditing`).
 *
 * Note: a fullscreen/maximize button is intentionally omitted for now (FIRM-11000 follow-up,
 * out of scope) — the toolbar leaves room for it to be slotted in later. A pan/hand toggle is
 * also omitted because the viewer runs view-only, where dragging already pans.
 */
@Component({
  selector: 'app-file-preview-controls',
  templateUrl: './file-preview-controls.component.html',
  styleUrls: ['./file-preview-controls.component.scss'],
  imports: [SharedModule, DataSelectorDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePreviewControlsComponent {
  // Zoom
  readonly zoomPercent = input.required<number>();
  readonly canZoomIn = input.required<boolean>();
  readonly canZoomOut = input.required<boolean>();

  // Pages
  readonly currentPage = input.required<number>();
  readonly pageCount = input.required<number>();

  // Search
  readonly searchTotal = input.required<number>();
  readonly searchCurrent = input.required<number>();
  /** Whether the host file type carries a searchable text layer. Fail-safe: no search button unless explicitly enabled. */
  readonly canSearch = input(false);

  readonly zoomIn = output<void>();
  readonly zoomOut = output<void>();
  readonly fit = output<void>();
  readonly goToPage = output<number>();
  readonly nextPage = output<void>();
  readonly prevPage = output<void>();
  readonly nextMatch = output<void>();
  readonly prevMatch = output<void>();
  readonly clearSearch = output<void>();

  /** Raw keystrokes from the search field; debounced + deduped into `searchSubmit`. */
  private readonly _searchTerm$ = new Subject<string>();

  /**
   * Live search-as-you-type stream: debounced so we don't run a search on every keystroke, and
   * deduped so an unchanged term never re-runs the same search.
   */
  readonly searchSubmit = outputFromObservable(this._searchTerm$.pipe(applicationDebounceTime(), distinctUntilChanged()));

  /** Whether the top toolbar is expanded into its search state. */
  protected readonly searchOpen = signal(false);
  /** Whether the bottom page pill is showing its jump-to-page input. */
  protected readonly jumpEditing = signal(false);

  protected readonly hasMatches = computed(() => this.searchTotal() > 0);
  protected readonly atFirstPage = computed(() => this.currentPage() <= 1);
  protected readonly atLastPage = computed(() => this.currentPage() >= this.pageCount());

  private readonly _searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly _pageInput = viewChild<ElementRef<HTMLInputElement>>('pageInput');

  constructor() {
    // Focus the search input as soon as the search state renders it.
    effect(() => {
      if (this.searchOpen()) this._searchInput()?.nativeElement.focus();
    });
    // Focus + select the jump-to-page input when it appears.
    effect(() => {
      if (this.jumpEditing()) {
        const el = this._pageInput()?.nativeElement;
        el?.focus();
        el?.select();
      }
    });
  }

  protected openSearch(): void {
    this.searchOpen.set(true);
  }

  protected closeSearch(): void {
    this.searchOpen.set(false);
    // Reset the dedup baseline so re-opening and re-typing the same term searches again.
    this._searchTerm$.next('');
    this.clearSearch.emit();
  }

  /** Feeds each keystroke into the debounced/deduped `searchSubmit` stream. */
  protected onSearchInput(value: string): void {
    this._searchTerm$.next(value);
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      // Search runs live as you type; Enter just walks the matches.
      case 'Enter':
        event.preventDefault();
        if (event.shiftKey) {
          this.prevMatch.emit();
        } else {
          this.nextMatch.emit();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeSearch();
        break;
    }
  }

  protected openJump(): void {
    this.jumpEditing.set(true);
  }

  protected commitJump(value: string): void {
    this.jumpEditing.set(false);
    const page = Number(value);
    // Ignore non-numeric / out-of-range entries — the input reverts to currentPage on its own.
    if (!Number.isFinite(page) || page < 1 || page > this.pageCount() || !Number.isInteger(page)) return;
    if (page !== this.currentPage()) this.goToPage.emit(page);
  }

  protected onJumpKeydown(event: KeyboardEvent, value: string): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitJump(value);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.jumpEditing.set(false);
    }
  }
}
